import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Transaction, Conta } from "@/types/database"
import { ajustarDataValida } from './dateUtils'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// Função utilitária para calcular a data efetiva de transações (considerando cartão de crédito)
export function getEffectiveTransactionDate(transaction: Transaction, accounts: Conta[] = []): Date {
  // console.log('getEffectiveTransactionDate: transaction', transaction);
  
  // Verificar se é uma transação de pagamento de fatura de cartão de crédito
  // Essas transações devem ser ignoradas nos relatórios para evitar duplicação
  // Verificamos se:
  // 1. É uma saída (despesa)
  // 2. A descrição segue o padrão específico de pagamento de fatura: "Pagamento fatura [nome do cartão] - [mês]/[ano]"
  if (
    transaction.tipo === 'saida' && 
    transaction.descricao?.match(/^Pagamento fatura .+ - \w+\/\d{4}$/)
  ) {
    // Retornar null para que seja filtrada nos relatórios
    return null as unknown as Date;
  }

  // Para todas as transações, usar a data de pagamento se disponível
  // Isso inclui cartões de crédito: ao pagar a fatura, os gastos movem para o mês de pagamento nos gráficos
  if (transaction.foi_paga && transaction.data_pagamento) {
    return parseTransactionDateUTC(transaction.data_pagamento);
  }

  const account = accounts.find(acc => acc.id === transaction.conta_id);
  const isCreditCard = account?.tipo === 'cartao_credito';

  // Para transações de cartão de crédito não pagas, usar a data de vencimento da fatura
  if (isCreditCard && account.dia_vencimento) {
    return calculateInvoiceDueDate(
      parseTransactionDateUTC(transaction.data),
      account.dia_vencimento,
      account.dia_fechamento
    );
  }

  return parseTransactionDateUTC(transaction.data);
}

// Função para tratar datas como UTC para evitar shift de fuso horário
export function parseTransactionDateUTC(dateString: string): Date {
  if (!dateString || typeof dateString !== 'string') {
    console.error('Data inválida:', dateString as unknown as string);
    return new Date();
  }
  try {
    // Tenta extrair apenas a parte da data (yyyy-MM-dd)
    // Isso resolve problemas tanto com "2026-02-01" quanto "2026-02-01T00:00:00..."
    // Se a string contiver 'T', pegamos o que vem antes. Se não, pegamos a string inteira.
    const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    
    // Verifica se temos um formato de data válido (yyyy-MM-dd)
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-').map(Number);
      // Retornar data local para evitar problemas de fuso horário na visualização
      // Antes estava retornando UTC, o que causava shift de dia para fusos negativos (ex: Brasil)
      // Ao usar o construtor new Date(y, m, d), criamos a data à meia-noite local
      return new Date(y, m - 1, d);
    }
    
    // Fallback para parseISO se não conseguirmos extrair yyyy-MM-dd
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.error('Data inválida após parse:', dateString);
      return new Date();
    }
    return date;
  } catch {
    console.error('Erro ao parsear data:', dateString);
    return new Date();
  }
}

// Função síncrona para calcular data de vencimento da fatura
export function calculateInvoiceDueDate(transactionDate: Date, diaVencimento: number, diaFechamento?: number): Date {
  try {
    if (!transactionDate || isNaN(transactionDate.getTime())) {
      console.error('Data de transação inválida:', transactionDate);
      return new Date();
    }
    
    if (typeof diaVencimento !== 'number' || diaVencimento < 1 || diaVencimento > 31) {
      console.error('Dia de vencimento inválido:', diaVencimento);
      return new Date();
    }
    
    const mes = transactionDate.getMonth() + 1; // 1-12
    const ano = transactionDate.getFullYear();
    
    let mesReferencia = mes;
    let anoReferencia = ano;
    
    if (diaFechamento) {
      // ✅ Usar função segura para criar data de fechamento
      const dataFechamento = ajustarDataValida(ano, mes, diaFechamento);
      
      if (transactionDate > dataFechamento) {
        if (mes === 12) {
          mesReferencia = 1;
          anoReferencia = ano + 1;
        } else {
          mesReferencia = mes + 1;
        }
      }
    } else {
      // Se não houver dia de fechamento, usar o dia de vencimento como referência
      // Se a data da transação for depois do dia de vencimento, jogar para o próximo mês
      if (transactionDate.getDate() > diaVencimento) {
        if (mes === 12) {
          mesReferencia = 1;
          anoReferencia = ano + 1;
        } else {
          mesReferencia = mes + 1;
        }
      }
    }
    
    // ✅ Usar função segura para criar data de vencimento
    return ajustarDataValida(anoReferencia, mesReferencia, diaVencimento);
  } catch (error) {
    console.error('Erro ao calcular data de vencimento:', error);
    return new Date();
  }
}
